/**
 * Simplified custom hook for handling file selection and preview generation
 */

import type { ClassificationContent } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import { setFileUploading, setClassificationError } from '@classification-testing/state/actions';
import { transformErrors } from '@core/adapters/errors';
import { ErrorCategory, type DomainError } from '@core/domain/errors';
import { track } from '@core/logging';
import { useAppDispatch } from '@store';
import { useCallback, useState } from 'react';

interface UseFileUploadProps {
  onContentChange: (content: ClassificationContent) => void;
  onFileReady?: (file: File) => void;
}

interface UseFileUploadResult {
  handleFileSelect: (file: File) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaste: (e: React.ClipboardEvent) => void;
  error: DomainError | null;
}

/**
 * Reads a file and returns the appropriate content based on file type
 */
async function processFile(file: File): Promise<ClassificationContent> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = e => {
      const result = e.target?.result as string;

      if (file.type.startsWith('image/')) {
        resolve({
          type: ContentType.IMAGE,
          imageUrl: result,
          imageName: file.name,
        });
      } else if (file.type.startsWith('audio/')) {
        resolve({
          type: ContentType.AUDIO,
          audioUrl: result,
          audioName: file.name,
        });
      } else if (file.type === 'text/plain' || file.type === 'application/json' || file.name.endsWith('.txt')) {
        resolve({
          type: ContentType.TEXT,
          text: result,
        });
      } else {
        resolve({
          type: ContentType.TEXT,
          text: `File content not supported for direct preview: ${file.name} (${file.type})`,
        });
      }
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    // Use appropriate FileReader method based on file type
    if (file.type.startsWith('image/') || file.type.startsWith('audio/')) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsText(file);
    }
  });
}

export function useFileUpload({ onContentChange, onFileReady }: UseFileUploadProps): UseFileUploadResult {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<DomainError | null>(null);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file) return;

      // Clear any previous errors
      setError(null);
      dispatch(setClassificationError(null));

      // Set loading state
      dispatch(setFileUploading(true));

      try {
        track('ProcessFileInitiated', {
          fileType: file.type,
          fileSize: file.size.toString(),
        });

        const content = await processFile(file);

        onContentChange(content);

        // Pass the file separately for upload operations
        if (onFileReady && (file.type.startsWith('image/') || file.type.startsWith('audio/'))) {
          track('ProcessFileSucceeded', {
            fileType: file.type,
            fileSize: file.size.toString(),
          });

          onFileReady(file);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process file';
        const domainError: DomainError = {
          code: 'FILE_PROCESSING_ERROR',
          message: errorMessage,
          userMessage: errorMessage,
          category: ErrorCategory.SERVICE,
        };

        track('ProcessFileFailed', {
          fileType: file.type,
          fileSize: file.size.toString(),
          errorMessage,
        });

        setError(domainError);
        dispatch(setClassificationError(transformErrors(domainError)));
      } finally {
        dispatch(setFileUploading(false));
      }
    },
    [onContentChange, onFileReady, dispatch]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData.items;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();

          if (file) {
            handleFileSelect(file);

            return;
          }
        }
      }
      // If no image found, let default paste behavior handle text
    },
    [handleFileSelect]
  );

  return {
    handleFileSelect,
    handleFileInputChange,
    handlePaste,
    error,
  };
}
